import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const SEV_COLOR = {
  critical: "bg-red-600",
  high: "bg-amber-500",
  medium: "bg-yellow-400",
  low: "bg-teal-600",
};

export default function NotificationBell() {
  const [data, setData] = useState({ notifications: [], unread: 0 });

  const load = useCallback(() => {
    api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="notification-bell" className="relative p-2 hover:bg-slate-50 border border-transparent hover:border-border transition-colors">
          <Bell className="h-[18px] w-[18px] text-slate-600" strokeWidth={2} />
          {data.unread > 0 && (
            <span data-testid="notification-unread-count"
              className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] bg-red-600 text-white text-[10px] font-bold flex items-center justify-center px-1">
              {data.unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-none w-96 p-0" data-testid="notification-dropdown">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--brand-primary)]">Notifications</span>
          <span className="text-[11px] font-mono text-muted-foreground">{data.unread} unread</span>
        </div>
        <div className="max-h-80 overflow-y-auto divide-y divide-border">
          {data.notifications.length === 0 && (
            <p className="text-[13px] text-muted-foreground text-center py-8">No notifications</p>
          )}
          {data.notifications.slice(0, 6).map((n) => (
            <div key={n.id} className={`px-4 py-3 ${n.read ? "opacity-60" : "bg-slate-50/60"}`}>
              <div className="flex items-start gap-2.5">
                <span className={`h-2 w-2 mt-1.5 shrink-0 ${SEV_COLOR[n.severity] || "bg-slate-400"}`} />
                <div>
                  <p className="text-[13px] font-medium text-slate-800">{n.title}</p>
                  <p className="text-[12px] text-muted-foreground mt-0.5">{n.message}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <Link to="/notifications" data-testid="notifications-view-all"
          className="block text-center text-[12px] font-medium text-teal-700 py-2.5 border-t border-border hover:bg-slate-50">
          View all notifications
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
